import React, { createContext, useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

// reducer - state management
import { dispatch, useSelector } from 'store';
import {
    appForm,
    updateStep,
    updateAccordion,
    updateStatus,
    updateUploadStatus,
    setSuccess,
    clearData,
    setEditApp,
    updateOnEdit,
    startNewApp,
    updateApp,
    getDropdownOpts,
    setEdit,
    resetTriggerSave,
    setAssignment,
    setImdaRef,
    setPayment,
    setAutoFill,
    removeIsStarting,
    setFormatOptions,
    setCARegistrationDetails
} from 'store/slices/application.slice';
import { getPostalData } from 'store/slices/postal.slice';
// project imports
import {
    requestDetails,
    requestEdit,
    requestEDH,
    requestMyInfo,
    requestMyInfoBasic,
    requestMyInfoMock,
    getActiveDistributionLicenceByUen,
    requestCARegistrationDetails,
    requestCAProfileDetails
} from 'store/api/application.api';
import {
    ContextType,
    DetailsType,
    Industry,
    FormProperties,
    FormData,
    FieldValue,
    FormSection,
    Field,
    Attachment
} from 'types/application';
import { formatRetrieve, formatSave, formatAutofill } from 'features/applicationPage/payload';
import { CASE_TYPE_ID, CONSTANTS } from 'common/constants';
import { INDUSTRIES } from 'common/application.const';
import { handleRequiredChecker } from 'features/applicationPage/helpers';

// ==============================|| CONTEXT & PROVIDER ||============================== //

const Context = createContext<ContextType | null>(null);

export function Provider({ children, action }: { children: React.ReactElement; action?: string }) {
    const state = useSelector(appForm);
    const postalState = useSelector((state) => state.postal);
    const { user } = useSelector((state) => state.account);
    const { CAProfileUUID } = useSelector((state) => state.appForm);
    const location = useLocation();
    const [details, setDetails] = useState<DetailsType>({
        forms: [],
        hasStarted: false
    });
    const [detailsTemp, setDetailsTemp] = useState<DetailsType>({
        forms: [],
        hasStarted: false
    });
    const [myInfoFormatted, setMyInfoFormatted] = useState<FormData | null>(null);
    const [successHasRendered, setSuccessHasRendered] = useState<boolean>(false);
    const [createTime, setCreateTime] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [content, setContent] = useState();

    const {
        isCaseRetrieve,
        selectedTitleSearch,
        isDeleteMode,
        caseTypeID,
        application,
        isSuccess,
        isOnEdit,
        applicationForm,
        applicationFormDescription,
        applicationType,
        activeStep,
        dropdownOpts,
        steps,
        isAmendMode,
        amendType,
        renewCaseType,
        cancelType,
        existingTitle
    } = state;

    const FEP_CASETYPE_MAP: Record<string, string> = {
        'Radio Operator Exam': 'ROE',
        'Accreditation of Certification Authority': 'ACA',
        'Registration of Accounting Authority': 'AA'
    };

    // Get suffix from applicationType first, or fallback to checking application string or content
    let suffix = FEP_CASETYPE_MAP[applicationType];
    if (!suffix && application) {
        if (application.includes('ACA')) suffix = 'ACA';
        else if (application.includes('AA')) suffix = 'AA';
        else if (application.includes('ROE')) suffix = 'ROE';
    }
    // If still no suffix and we have content from backend, use that
    if (!suffix && content) {
        suffix = FEP_CASETYPE_MAP[(content as any)?.licenceName];
    }

    let industry: any;
    // For ACA/AA in special modes, select the appropriate industry configuration (frontend-only differentiation)
    // Backend still uses generic IMDA-IRIS-Work-LICApplication caseType
    // Use Redux state fields which are now properly cleared when switching between modes
    if (suffix === 'ACA' && (isAmendMode || amendType)) {
        industry = (INDUSTRIES as Industry[]).find((ind: Industry) => ind.caseType === 'IMDA-IRIS-Work-LICApplication-ACA-AMEND');
    } else if (suffix === 'ACA' && renewCaseType) {
        // renewCaseType is set only during renew flow and cleared for other modes
        industry = (INDUSTRIES as Industry[]).find((ind: Industry) => ind.caseType === renewCaseType);
    } else if (cancelType === 'ACA-CANCEL') {
        industry = (INDUSTRIES as Industry[]).find((ind: Industry) => ind.caseType === 'IMDA-IRIS-Work-LICApplication-ACA-CANCEL');
    } else if (cancelType === 'AA-CANCEL') {
        industry = (INDUSTRIES as Industry[]).find((ind: Industry) => ind.caseType === 'IMDA-IRIS-Work-LICApplication-AA-CANCEL');
    } else if (suffix === 'ROE') {
        industry = (INDUSTRIES as Industry[]).find((ind: Industry) => ind.caseType === 'IMDA-IRIS-Work-LICApplication-ROE');
    } else if (suffix === 'ACA') {
        industry = (INDUSTRIES as Industry[]).find((ind: Industry) => ind.caseType === 'IMDA-IRIS-Work-LICApplication-ACA');
    } else if (suffix === 'AA') {
        industry = (INDUSTRIES as Industry[]).find((ind: Industry) => ind.caseType === 'IMDA-IRIS-Work-LICApplication-AA');
    } else {
        industry = (INDUSTRIES as Industry[]).find((ind: Industry) => ind.caseType === application);
    }

    useEffect(() => {
        if (selectedTitleSearch && !isDeleteMode) {
            setDetails(detailsTemp);
        }
    }, [selectedTitleSearch]);

    useEffect(() => {
        const fetchDetails = async () => {
            dispatch(updateStatus(CONSTANTS.LOADING));
            let myInfoContent;
            let CAProfileDetails;
            //  && state.isAppStarting add this to condition for higher env
            if (user?.role === 'singpass') {
                myInfoContent = await requestMyInfo(user?.id, state.myinfoCode, state.myinfoState);
                dispatch(removeIsStarting());
            }
            if (
                user?.role === 'singpass' &&
                applicationType === 'Content Assessor (CA) Registration/Renewal' &&
                applicationForm === 'FMUPD'
            ) {
                CAProfileDetails = await requestCAProfileDetails(user?.id);
            }
            if (
                user?.role === 'corppass' &&
                (industry?.caseType === 'IMDA-IRIS-Work-Publication' ||
                    industry?.caseType === 'IMDA-IRIS-Work-Arts' ||
                    industry?.caseType === CASE_TYPE_ID.FILMS ||
                    industry?.caseType === CASE_TYPE_ID.VIDEO_GAMES ||
                    industry?.caseType === CASE_TYPE_ID.INTERACTIVE_SOFTWARE ||
                    industry?.caseType === 'IMDA-IRIS-Work-LICApplication-ROE' ||
                    industry?.caseType === 'IMDA-IRIS-Work-LICApplication-ACA' ||
                    industry?.caseType === 'IMDA-IRIS-Work-LICApplication-ACA-AMEND' ||
                    industry?.caseType === 'IMDA-IRIS-Work-LICApplication-ACA-RENEW' ||
                    industry?.caseType === 'IMDA-IRIS-Work-LICApplication-ACA-RENEW-NOPAY' ||
                    industry?.caseType === 'IMDA-IRIS-Work-LICApplication-ACA-CANCEL' ||
                    industry?.caseType === 'IMDA-IRIS-Work-LICApplication-AA' ||
                    industry?.caseType === 'IMDA-IRIS-Work-LICApplication-AA-CANCEL')
            ) {
                dispatch(setAutoFill());
                // const data = corppassMockData[0];
                // // const addressTemp = data.addresses?.find((address: any) => address?.purpose?.value === 'registered');
                // // const corppassTemp = data.corppass?.find((user: any) => user?.['corppass-user']?.['id-no']?.value === nric);
                // const dataContent = {
                //     'basic-profile': data['basic-profile'],
                //     address: data.addresses[0],
                //     corppass: data.corppass[0]
                // };
                myInfoContent = await requestEDH(user?.uen, user?.id);
                // myInfoContent = dataContent;
            }

            const formattedForms = await formatAutofill({
                myInfoContent,
                application,
                applicationType,
                uen: user?.uen,
                id: user?.id,
                userRole: user?.role ?? ''
            });

            // Sanitize undefined values in autofill (some salutation fields may be undefined)
            if (formattedForms && (formattedForms as any).values) {
                const cleanedValues = Object.entries((formattedForms as any).values).reduce((acc: any, [k, v]) => {
                    if (v !== undefined) acc[k] = v;
                    return acc;
                }, {} as any);
                setMyInfoFormatted({ ...(formattedForms as any), values: cleanedValues });
            } else {
                setMyInfoFormatted(null);
            }

            const isActiveDistributionLicenceCompany =
                applicationType === 'Film Classification for Public Exhibition' ||
                applicationType === 'Film Classification for Public Exhibition - Non Profit'
                    ? []
                    : await getActiveDistributionLicenceByUen(user?.uen);

            let response;

            if (action === 'download') {
                response = {
                    content: {
                        licenceName: applicationType,
                        createTime: new Date()
                    }
                };

                if (
                    application === CASE_TYPE_ID.VIDEO_GAMES ||
                    application === CASE_TYPE_ID.FILMS ||
                    application === 'IMDA-FVGCS-Work-FVG-FilmTitles'
                ) {
                    response = await requestDetails(caseTypeID);
                }
            } else {
                response = await requestDetails(caseTypeID);
            }

            if (response.assignments) {
                // if (
                //     (!!isOnEdit && response.assignments[0].name === 'Review') ||
                //     response.assignments[0].name === 'Review Publication Screen'
                // ) {
                //     dispatch(clearData());
                //     await requestEdit(response.ID);
                // }
                // if (!!isOnEdit && response.assignments[0].name === 'Complete Payment') {
                //     dispatch(clearData());
                //     await requestEdit(response.ID);
                //     await requestEdit(response.ID);
                // }
                // Static actionCollect as of now need further planning
                // const action = response.assignments[0].actions.length === 1 ? response.assignments[0].actions[0].ID : 'EventEditDetails';
                const action = response.assignments[0].actions[0].ID || 'ArtsPOAction';
                dispatch(setAssignment({ assignmentID: response.assignments[0].ID, actionCollect: action }));
            }
            // if (!isSuccess) dispatch(getDropdownOpts());

            if (Object.keys(response).length > 0) {
                dispatch(updateUploadStatus(CONSTANTS.LOADING));
                const content = response.content;
                setContent(content);
                const newApplication = true;

                // Detect amend mode from backend content (when resuming draft)
                // Check if transactionType is 'Amend' in the content
                const isAmendCase = content?.transactionType === 'Amend';

                // If this is an amend case but Redux state doesn't know yet, update it
                if (isAmendCase && !isAmendMode && suffix === 'ACA') {
                    // Determine amendType from iWantToUpdate array
                    // Note: iWantToUpdate now only contains selected items (no pySelected property)
                    const iWantToUpdate = content?.LICApplication?.licence?.licenceDetails?.iWantToUpdate || [];
                    const materialSelected = iWantToUpdate.some((item: any) => item.mixedValue === 'Material Fields');
                    const nonMaterialSelected = iWantToUpdate.some((item: any) => item.mixedValue === 'Non Material Fields');

                    let amendTypeFromContent: 'Material' | 'Non-Material' | 'Both' = 'Both';
                    if (materialSelected && nonMaterialSelected) {
                        amendTypeFromContent = 'Both';
                    } else if (materialSelected) {
                        amendTypeFromContent = 'Material';
                    } else if (nonMaterialSelected) {
                        amendTypeFromContent = 'Non-Material';
                    }

                    dispatch(
                        setEditApp({
                            isAmendMode: true,
                            amendType: amendTypeFromContent
                        })
                    );
                }

                // For amend/cancel/renew mode, pass the correct identifier to formatRetrieve for correct form ID mapping
                // even though the backend caseType is still the generic IMDA-IRIS-Work-LICApplication
                // Use Redux state to determine mode - these are properly cleared when switching modes
                // Check if this is ACA application using multiple sources (some may be undefined during initial load)
                const isACAApplication =
                    content?.licenceName === 'Accreditation of Certification Authority' ||
                    applicationType === 'Accreditation of Certification Authority' ||
                    (application && application.includes('ACA'));

                const isAmendApplication =
                    (application && application.includes('-AMEND')) || (isACAApplication && (isAmendMode || isAmendCase));
                const isRenewApplication = (application && application.includes('-RENEW')) || renewCaseType !== undefined;

                let applicationForFormatting = application;
                if (isAmendApplication && isACAApplication) {
                    applicationForFormatting = 'IMDA-IRIS-Work-LICApplication-ACA-AMEND';
                } else if (cancelType === 'ACA-CANCEL') {
                    applicationForFormatting = 'IMDA-IRIS-Work-LICApplication-ACA-CANCEL';
                } else if (cancelType === 'AA-CANCEL') {
                    applicationForFormatting = 'IMDA-IRIS-Work-LICApplication-AA-CANCEL';
                } else if (isRenewApplication && isACAApplication) {
                    // Use renewCaseType from Redux state if available (includes NOPAY suffix if applicable)
                    applicationForFormatting = renewCaseType || 'IMDA-IRIS-Work-LICApplication-ACA-RENEW';
                }

                const formattedForms = await formatRetrieve(
                    content,
                    applicationForFormatting,
                    caseTypeID,
                    action,
                    applicationForm,
                    applicationType,
                    user?.role,
                    selectedTitleSearch,
                    dropdownOpts,
                    newApplication,
                    isActiveDistributionLicenceCompany,
                    CAProfileDetails
                );

                // For ACA amend drafts, extract amendType from formatRetrieve result if it returns an object
                // Handle both array (legacy) and object { forms, amendType } return formats
                let formsArray: any[] = [];

                // Check if result is an object with 'forms' property (new format)
                if (formattedForms && typeof formattedForms === 'object' && !Array.isArray(formattedForms) && 'forms' in formattedForms) {
                    // Extract forms array from object
                    formsArray = (formattedForms as any).forms || [];

                    // If ACA application, also check for amendType
                    if (isACAApplication && 'amendType' in formattedForms) {
                        const amendTypeFromRetrieve = (formattedForms as any).amendType;

                        if (amendTypeFromRetrieve && amendTypeFromRetrieve !== amendType) {
                            // Update Redux with the amendType from backend
                            dispatch(
                                setEditApp({
                                    amendType: amendTypeFromRetrieve
                                })
                            );
                        }
                    }
                } else if (Array.isArray(formattedForms)) {
                    // Legacy format - already an array
                    formsArray = formattedForms;
                }

                if (formsArray && formsArray.length > 0) dispatch(updateUploadStatus(CONSTANTS.FULFILLED));

                const formatted: DetailsType = {
                    forms: formsArray?.map((form: FormData) => {
                        const valuesDefaultEmpty = Object.keys(form.values as { [key: string]: FieldValue }).reduce(
                            (values: { [key: string]: FieldValue }, key: string) => {
                                // Preserve falsy values like 0, false, empty string - only default to '' if undefined/null
                                values[key] =
                                    form.values && form.values[key] !== undefined && form.values[key] !== null ? form.values[key] : '';
                                return values;
                            },
                            {}
                        );
                        const info = (industry?.forms as FormProperties[])?.find(
                            (formInfo: FormProperties) => formInfo.formID === form.formID
                        );
                        const isSingpass = !user?.uen;
                        form.hasError = false;
                        form.isRequiredIncomplete = valuesDefaultEmpty
                            ? handleRequiredChecker(valuesDefaultEmpty, info, false, isSingpass)
                            : true;
                        form.values = valuesDefaultEmpty;
                        return form;
                    }),
                    hasStarted: false
                };

                if (application === CASE_TYPE_ID.VIDEO_GAMES && activeStep === 4) {
                    setDetails({
                        forms: formatted.forms.filter((i) => i.formID.toLowerCase().includes('appsubmission')),
                        hasStarted: true
                    });
                } else if (application === CASE_TYPE_ID.VIDEO_GAMES && activeStep === 3) {
                    setDetails({
                        forms: formatted.forms.filter((i) => i.formID.toLowerCase().includes('content')),
                        hasStarted: true
                    });
                } else {
                    setDetails(formatted);
                }

                setCreateTime(response.createTime);

                dispatch(updateStatus(CONSTANTS.FULFILLED));
            } else {
                dispatch(updateStatus(CONSTANTS.FAILED));
            }
        };

        const fetchCARegistrationDetails = async () => {
            const response = await requestCARegistrationDetails(caseTypeID);
            dispatch(setCARegistrationDetails(response));
        };

        if ((!!caseTypeID && (details.forms.length === 0 || isSuccess) && !successHasRendered) || isCaseRetrieve) {
            fetchDetails();

            if (isSuccess) {
                setSuccessHasRendered(true);
            }

            if (isSuccess && applicationType === 'Content Assessor (CA) Registration/Renewal') {
                fetchCARegistrationDetails();
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        caseTypeID,
        applicationForm,
        applicationFormDescription,
        // Don't include 'details' or 'detailsTemp' here - they cause infinite loops
        // because fetchDetails() updates them
        (application === CASE_TYPE_ID.VIDEO_GAMES || CASE_TYPE_ID.FILMS) && details,
        (application === CASE_TYPE_ID.VIDEO_GAMES || CASE_TYPE_ID.FILMS) && detailsTemp,
        application,
        isSuccess,
        successHasRendered,
        isOnEdit,
        // Added for ACA renew/amend mode detection
        location.pathname
    ]);

    const handleShowLoading = () => {
        setLoading(true);
    };
    const handleRemoveLoading = () => {
        setLoading(false);
    };
    const value = useMemo(() => {
        const handleSave = async (isSubmit?: boolean) => {
            const displayLoading = [state.status, postalState.postalStatus, state.dropdownStatus, state.uploadStatus].includes(
                CONSTANTS.LOADING
            );
            if (!displayLoading) {
                dispatch(updateStatus(CONSTANTS.LOADING));
                const formattedPayload: any = await formatSave({
                    details,
                    application: state.application,
                    caseTypeID: state.caseTypeID,
                    action,
                    userRole: user?.role,
                    applicationForm: applicationForm || applicationFormDescription,
                    applicationType: state.applicationType,
                    activeStep,
                    content,
                    selectedTitleSearch,
                    amendType: amendType,
                    CAProfileUUID,
                    existingTitle
                });

                setDetailsTemp((prev) => {
                    const forms = prev.forms.map((form: FormData) => {
                        const valuesTemp = { ...form.values };
                        const info = (industry?.forms as FormProperties[])?.find(
                            (formInfo: FormProperties) => formInfo.formID === form.formID
                        );

                        // get file uploads fields
                        const fileUploadFields = info?.fields?.reduce((ids: string[], section: FormSection) => {
                            const idsFile = section.fieldList
                                .filter((field: Field) => field.type === 'file')
                                .map((field: Field) => `${section.sectionID}-${field.id}`);
                            const idsHelperList = section.fieldList
                                .filter((field: Field) => field.type === 'helperList')
                                .map((field: Field) => `${section.sectionID}-${field.id}-${field.mainField?.id}`);
                            return [...ids, ...idsFile, ...idsHelperList];
                        }, []);

                        fileUploadFields?.forEach((key: string) => {
                            const value = ((valuesTemp[key] as Attachment[]) || [])
                                .filter((attachment: Attachment) => !attachment.deleted)
                                .map((attachment: Attachment) => {
                                    const attchTemp = { ...attachment };
                                    attchTemp.saved = true;
                                    return attchTemp;
                                });
                            valuesTemp[key] = value;
                        });

                        // get table fields
                        const tableFields = info?.fields?.reduce((ids: string[], section: FormSection) => {
                            const idsTemp = section.fieldList
                                .filter((field: Field) => field.type === 'table')
                                .map((field: Field) => `${section.sectionID}-${field.id}`);
                            return [...ids, ...idsTemp];
                        }, []);

                        tableFields?.forEach((key: string) => {
                            const value = ((valuesTemp[key] as { [key: string]: FieldValue }[]) || [])
                                .filter((row: { [key: string]: FieldValue }) => !row.deleted)
                                .map((row: { [key: string]: FieldValue }) => {
                                    const rowTemp = { ...row };
                                    rowTemp.uploaded = 'true';
                                    return rowTemp;
                                });
                            valuesTemp[key] = value;
                        });

                        const formTemp = { ...form };
                        formTemp.values = valuesTemp;
                        return formTemp;
                    });

                    return {
                        ...prev,
                        forms
                    };
                });

                setDetails((prev) => {
                    const forms = prev.forms.map((form: FormData) => {
                        const valuesTemp = { ...form.values };
                        const info = (industry?.forms as FormProperties[])?.find(
                            (formInfo: FormProperties) => formInfo.formID === form.formID
                        );

                        // get file uploads fields
                        const fileUploadFields = info?.fields?.reduce((ids: string[], section: FormSection) => {
                            const idsFile = section.fieldList
                                .filter((field: Field) => field.type === 'file')
                                .map((field: Field) => `${section.sectionID}-${field.id}`);
                            const idsHelperList = section.fieldList
                                .filter((field: Field) => field.type === 'helperList')
                                .map((field: Field) => `${section.sectionID}-${field.id}-${field.mainField?.id}`);
                            return [...ids, ...idsFile, ...idsHelperList];
                        }, []);

                        fileUploadFields?.forEach((key: string) => {
                            const value = ((valuesTemp[key] as Attachment[]) || [])
                                .filter((attachment: Attachment) => !attachment.deleted)
                                .map((attachment: Attachment) => {
                                    const attchTemp = { ...attachment };
                                    attchTemp.saved = true;
                                    return attchTemp;
                                });
                            valuesTemp[key] = value;
                        });

                        // get table fields
                        const tableFields = info?.fields?.reduce((ids: string[], section: FormSection) => {
                            const idsTemp = section.fieldList
                                .filter((field: Field) => field.type === 'table')
                                .map((field: Field) => `${section.sectionID}-${field.id}`);
                            return [...ids, ...idsTemp];
                        }, []);

                        tableFields?.forEach((key: string) => {
                            const value = ((valuesTemp[key] as { [key: string]: FieldValue }[]) || [])
                                .filter((row: { [key: string]: FieldValue }) => !row.deleted)
                                .map((row: { [key: string]: FieldValue }) => {
                                    const rowTemp = { ...row };
                                    rowTemp.uploaded = 'true';
                                    return rowTemp;
                                });
                            valuesTemp[key] = value;
                        });

                        const formTemp = { ...form };
                        formTemp.values = valuesTemp;
                        return formTemp;
                    });

                    return {
                        ...prev,
                        forms
                    };
                });

                return dispatch(updateApp({ payload: formattedPayload, isSubmit, action: details?.forms[0]?.formID }));
            }
            return false;
        };
        const handleSavePageInstruction = async (pageInstructions?: any[]) => {
            const displayLoading = [state.status, postalState.postalStatus, state.dropdownStatus, state.uploadStatus].includes(
                CONSTANTS.LOADING
            );
            if (!displayLoading) {
                const formattedPayload = await formatSave({
                    details,
                    application: state.application,
                    caseTypeID: state.caseTypeID,
                    action,
                    pageInstructions,
                    applicationType: state.applicationType
                });
                return dispatch(updateApp({ payload: formattedPayload, isSubmit: false }));
            }
            return false;
        };
        return {
            ...state,
            postalList: postalState.postalList,
            postalStatus: postalState.postalStatus,
            updateStep,
            updateAccordion,
            updateStatus,
            updateUploadStatus,
            setSuccess,
            clearData,
            setEditApp,
            updateOnEdit,
            startNewApp,
            updateApp,
            getDropdownOpts,
            getPostalData,
            setEdit,
            resetTriggerSave,
            setAssignment,
            setImdaRef,
            setPayment,

            action,
            industry,
            createTime,
            details,
            detailsTemp,
            setDetails,
            handleSave,
            handleSavePageInstruction,
            loading,
            handleShowLoading,
            handleRemoveLoading,
            myInfoFormatted,
            isOnEdit,
            applicationForm
        };
    }, [
        state,
        action,
        industry,
        applicationForm,
        createTime,
        details,
        loading,
        myInfoFormatted,
        user?.role,
        postalState.postalStatus,
        postalState.postalList,
        isOnEdit
        // isStatusDeclared,
        // isSubmissionRequired
    ]);

    return <Context.Provider value={value}>{children}</Context.Provider>;
}

export default Context;
